import React, { useState, useRef, useEffect, useCallback } from 'react';
import FAQ_DATA from './chatFAQ.json';

const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

function formatTime(date) {
  return (date instanceof Date ? date : new Date(date))
    .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function FormattedAnswer({ text }) {
  return (
    <span>
      {(text || '').split('\n').map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j}>{part.slice(2, -2)}</strong>
              : part
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

const QUICK_REPLIES = [
  'How do I post an ad?',
  'How do I contact a seller?',
  'How does payment work?',
  'How do I verify my account?',
  'How do I reset my password?',
  'How do I report a seller?',
];

// ── Local FAQ search (instant fallback when offline or no token) ──────────
const FAQ = FAQ_DATA.faqs || [];

function searchFAQ(question) {
  const q = question.toLowerCase().trim();
  // 1. Exact match
  let match = FAQ.find(item => item.question?.toLowerCase() === q);
  if (match) return { answer: match.answer, matched: true };
  // 2. Keyword match
  for (const item of FAQ) {
    const hasKeyword = (item.keywords || []).some(k => q.includes(k.toLowerCase()));
    if (hasKeyword) return { answer: item.answer, matched: true };
  }
  // 3. Fuzzy word match
  const words = q.split(/\s+/).filter(w => w.length > 3);
  for (const item of FAQ) {
    if (!item.question) continue;
    const matchCount = words.filter(w => item.question.toLowerCase().includes(w)).length;
    if (matchCount >= 2) return { answer: item.answer, matched: true };
  }
  return {
    answer: "I'm not sure about that. Please try one of the questions below or contact **support@imselling.co.ke** for help.",
    matched: false,
  };
}

// ── ChatWidget ────────────────────────────────────────────────────────────
const ChatWidget = () => {
  const [chatOpen,   setChatOpen]   = useState(false);
  const [input,      setInput]      = useState('');
  const [messages,   setMessages]   = useState([]);
  const [showQuick,  setShowQuick]  = useState(true);
  const [isTyping,   setIsTyping]   = useState(false);
  const [sessionId,  setSessionId]  = useState(null);
  const [histLoaded, setHistLoaded] = useState(false);
  const [unread,     setUnread]     = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setUnread(0);
    }
  }, [chatOpen]);

  // ── Load chat history from backend on first open ──────────────────────
  const loadHistory = useCallback(async () => {
    if (histLoaded) return;
    setHistLoaded(true);

    const token = getToken();
    if (!token) {
      // Not logged in — show welcome only
      setMessages([{
        id: 'welcome', role: 'bot',
        content: "Hi 👋 I'm the I'm Selling assistant. Choose a question below or type your own!",
        created_at: new Date().toISOString(),
      }]);
      return;
    }

    try {
      const res  = await fetch(`${API_BASE}/chat/history/`, {
        headers: { Authorization: `Token ${token}` },
      });
      const data = await res.json();
      if (data.success && data.messages?.length > 0) {
        setMessages(data.messages);
        setSessionId(data.session_id);
        setShowQuick(false);
      } else {
        setMessages([{
          id: 'welcome', role: 'bot',
          content: "Hi 👋 I'm the I'm Selling assistant. Choose a question below or type your own!",
          created_at: new Date().toISOString(),
        }]);
      }
    } catch {
      setMessages([{
        id: 'welcome', role: 'bot',
        content: "Hi 👋 I'm the I'm Selling assistant. Choose a question below or type your own!",
        created_at: new Date().toISOString(),
      }]);
    }
  }, [histLoaded]);

  useEffect(() => {
    if (chatOpen) loadHistory();
  }, [chatOpen, loadHistory]);

  // ── Send message ──────────────────────────────────────────────────────
  // Strategy:
  //   1. Show user message immediately (optimistic UI)
  //   2. If logged in → POST to backend (saves to DB, appears in admin panel)
  //   3. If not logged in → search local FAQ as fallback
  const handleSend = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || isTyping) return;

    setShowQuick(false);
    setInput('');

    // Add user message instantly
    const userMsg = {
      id: `u-${Date.now()}`, role: 'user',
      content: trimmed, created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    const token = getToken();

    // ── Path A: Logged in → send to backend ──────────────────────────
    if (token) {
      try {
        const res  = await fetch(`${API_BASE}/chat/message/`, {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ message: trimmed, session_id: sessionId }),
        });
        const data = await res.json();

        if (data.success) {
          if (data.session_id) setSessionId(data.session_id);

          setTimeout(() => {
            setIsTyping(false);
            const botMsg = {
              id: `b-${Date.now()}`, role: 'bot',
              content: data.reply, created_at: new Date().toISOString(),
            };
            setMessages(prev => [...prev, botMsg]);
            if (!chatOpen) setUnread(u => u + 1);
            if (!data.matched) setTimeout(() => setShowQuick(true), 800);
          }, 600 + Math.random() * 400);
          return; // ✅ Done — backend handled it
        }
        // Backend returned error — fall through to local FAQ
      } catch {
        // Network error — fall through to local FAQ
      }
    }

    // ── Path B: No token OR backend failed → local FAQ ────────────────
    setTimeout(() => {
      const result = token
        ? { answer: "Sorry, I couldn't connect right now. Please try again or contact **support@imselling.co.ke**.", matched: false }
        : searchFAQ(trimmed);

      setIsTyping(false);
      const botMsg = {
        id: `b-${Date.now()}`, role: 'bot',
        content: result.answer, created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, botMsg]);
      if (!chatOpen) setUnread(u => u + 1);
      if (!result.matched) setTimeout(() => setShowQuick(true), 800);
    }, 600 + Math.random() * 400);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      <style>{`
        .cw-root {
          position: fixed; bottom: 24px; right: 24px;
          z-index: 9990;
          font-family: 'DM Sans', -apple-system, sans-serif;
        }
        .cw-trigger {
          width: 56px; height: 56px; border-radius: 50%;
          background: linear-gradient(135deg, #075e54, #128C7E);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 20px rgba(7,94,84,0.4);
          transition: transform 0.2s, box-shadow 0.2s;
          color: white; font-size: 22px; position: relative;
        }
        .cw-trigger:hover { transform: scale(1.08); box-shadow: 0 6px 26px rgba(7,94,84,0.5); }
        .cw-unread {
          position: absolute; top: -4px; right: -4px;
          width: 20px; height: 20px; border-radius: 50%;
          background: #ef4444; color: white;
          font-size: 10px; font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          border: 2px solid white;
        }
        .cw-dialog {
          position: absolute; bottom: 68px; right: 0;
          width: 360px; height: 580px;
          max-height: calc(100vh - 100px);
          background: #e5ddd5; border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
          display: flex; flex-direction: column; overflow: hidden;
          animation: cwSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes cwSlideUp {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cw-header {
          flex-shrink: 0; height: 64px;
          background: #075e54; padding: 10px 14px;
          display: flex; align-items: center; gap: 10px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        }
        .cw-header-avatar {
          width: 38px; height: 38px; border-radius: 50%;
          background: linear-gradient(135deg, #0d9488, #6366f1);
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; flex-shrink: 0; position: relative;
        }
        .cw-header-online {
          position: absolute; bottom: 1px; right: 1px;
          width: 10px; height: 10px; border-radius: 50%;
          background: #25D366; border: 2px solid #075e54;
        }
        .cw-header-info { flex: 1; }
        .cw-header-name  { color: white; font-weight: 700; font-size: 14px; line-height: 1.2; }
        .cw-header-status{ color: rgba(255,255,255,0.75); font-size: 11px; margin-top: 1px; }
        .cw-header-close {
          background: rgba(255,255,255,0.12); border: none; border-radius: 50%;
          color: rgba(255,255,255,0.85); width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 16px; flex-shrink: 0; transition: background 0.15s;
        }
        .cw-header-close:hover { background: rgba(255,255,255,0.22); }
        .cw-body {
          flex: 1; overflow-y: auto; overflow-x: hidden;
          padding: 10px 10px 4px;
          display: flex; flex-direction: column; gap: 2px;
          background: #e5ddd5;
        }
        .cw-body::-webkit-scrollbar { width: 4px; }
        .cw-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
        .cw-date-chip { text-align: center; margin: 8px 0 4px; }
        .cw-date-chip span {
          background: rgba(255,255,255,0.85); border-radius: 8px;
          padding: 3px 10px; font-size: 10.5px; color: #667781; font-weight: 600;
          box-shadow: 0 1px 1px rgba(0,0,0,0.08);
        }
        .cw-bubble-wrap { display: flex; margin-bottom: 3px; align-items: flex-end; gap: 6px; }
        .cw-bubble-wrap.them { justify-content: flex-start; padding-right: 40px; }
        .cw-bubble-wrap.me   { justify-content: flex-end; padding-left: 40px; flex-direction: row-reverse; }
        .cw-bubble {
          max-width: 80%; min-width: 100px; padding: 7px 10px 5px; position: relative;
          box-shadow: 0 1px 1px rgba(0,0,0,0.13);
          word-break: break-word; white-space: pre-wrap;
          font-size: 13.5px; line-height: 1.55; color: #111b21;
        }
        .cw-bubble.them { background: #ffffff; border-radius: 0px 10px 10px 10px; }
        .cw-bubble.me   { background: #dcf8c6; border-radius: 10px 0px 10px 10px; }
        .cw-bubble.them::before {
          content: ''; position: absolute; top: 0; left: -6px;
          border: 8px solid transparent; border-width: 0 8px 8px 0;
          border-right-color: #ffffff;
        }
        .cw-bubble.me::before {
          content: ''; position: absolute; top: 0; right: -6px;
          border: 8px solid transparent; border-width: 0 0 8px 8px;
          border-left-color: #dcf8c6;
        }
        .cw-bubble-meta {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 3px; margin-top: 3px; font-size: 10px; color: #667781;
        }
        .cw-sender-label { font-size: 11px; font-weight: 700; margin-bottom: 2px; padding-left: 2px; }
        .cw-typing { display: flex; align-items: center; gap: 4px; padding: 8px 12px; }
        .cw-dot {
          width: 7px; height: 7px; border-radius: 50%; background: #94a3b8;
          animation: cwBounce 1.2s infinite ease-in-out;
        }
        .cw-dot:nth-child(1) { animation-delay: 0s; }
        .cw-dot:nth-child(2) { animation-delay: 0.2s; }
        .cw-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes cwBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30%            { transform: translateY(-5px); opacity: 1; }
        }
        .cw-quick { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 4px 4px; }
        .cw-quick-btn {
          background: white; border: 1.5px solid #25D366; border-radius: 20px;
          padding: 5px 12px; font-size: 12px; color: #075e54; cursor: pointer;
          font-weight: 600; transition: all 0.15s; font-family: inherit;
        }
        .cw-quick-btn:hover { background: #25D366; color: white; }
        .cw-input-area {
          flex-shrink: 0; min-height: 64px; max-height: 64px;
          background: #f0f2f5; padding: 8px 10px;
          display: flex; align-items: center; gap: 8px;
          border-top: 1px solid rgba(0,0,0,0.06);
        }
        .cw-input-wrap { flex: 1; }
        .cw-input {
          width: 100%; box-sizing: border-box;
          background: white; border: none; border-radius: 22px;
          padding: 9px 16px; font-size: 13.5px; outline: none;
          font-family: inherit; color: #111b21;
          box-shadow: 0 1px 2px rgba(0,0,0,0.08);
          resize: none; overflow: hidden;
        }
        .cw-input::placeholder { color: #8696a0; }
        .cw-send {
          width: 40px; height: 40px; border-radius: 50%;
          background: #e9edef; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #8696a0; transition: all 0.15s; flex-shrink: 0;
        }
        .cw-send.active { background: #075e54; color: white; }
        .cw-send.active:hover { background: #128C7E; }
        .cw-powered {
          flex-shrink: 0; height: 28px;
          text-align: center; font-size: 10px; color: #8696a0;
          padding: 4px; background: #f0f2f5;
          display: flex; align-items: center; justify-content: center;
        }
        @media (max-width: 768px) {
          .cw-root {
            right: 0;
            bottom: 0;
            left: 0;
            width: 100vw;
          }
          .cw-trigger {
            right: 16px;
            bottom: 16px;
            position: fixed;
          }
          .cw-dialog {
            position: fixed;
            bottom: 0;
            right: 0;
            left: 0;
            width: 100vw;
            max-width: 100vw;
            height: 85vh;
            max-height: 85vh;
            border-radius: 16px 16px 0 0;
          }
        }
      `}</style>

      <div className="cw-root">
        <button className="cw-trigger" onClick={() => setChatOpen(o => !o)} aria-label="Open support chat">
          {chatOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          ) : '💬'}
          {!chatOpen && unread > 0 && (
            <div className="cw-unread">{unread > 9 ? '9+' : unread}</div>
          )}
        </button>

        {chatOpen && (
          <div className="cw-dialog">
            <div className="cw-header">
              <div className="cw-header-avatar">
                🤖<span className="cw-header-online" />
              </div>
              <div className="cw-header-info">
                <div className="cw-header-name">I'm Selling Assistant</div>
                <div className="cw-header-status">{isTyping ? '● Typing…' : '● Online · 24/7'}</div>
              </div>
              <button className="cw-header-close" onClick={() => setChatOpen(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="cw-body">
              <div className="cw-date-chip"><span>TODAY</span></div>

              {messages.map((msg, i) => {
                const isBot = msg.role === 'bot';
                const senderChanged = i === 0 || messages[i - 1]?.role !== msg.role;
                const timeStr = msg.created_at ? formatTime(msg.created_at) : '';
                return (
                  <div key={msg.id || i} className={`cw-bubble-wrap ${isBot ? 'them' : 'me'}`}>
                    {isBot && senderChanged && (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#0d9488,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, marginBottom: 2 }}>🤖</div>
                    )}
                    {isBot && !senderChanged && <div style={{ width: 28, flexShrink: 0 }} />}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isBot ? 'flex-start' : 'flex-end' }}>
                      {senderChanged && (
                        <div className="cw-sender-label" style={{ color: isBot ? '#0d9488' : '#075e54' }}>
                          {isBot ? '🤖 AI Assistant' : 'You'}
                        </div>
                      )}
                      <div className={`cw-bubble ${isBot ? 'them' : 'me'}`}>
                        <FormattedAnswer text={msg.content} />
                        <div className="cw-bubble-meta">
                          {timeStr}
                          {!isBot && (
                            <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                              <path d="M1 6l3 3 5-5" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M5 6l3 3 5-5" stroke="#8696a0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {isTyping && (
                <div className="cw-bubble-wrap them">
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#0d9488,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>🤖</div>
                  <div className="cw-bubble them">
                    <div className="cw-typing">
                      <div className="cw-dot" /><div className="cw-dot" /><div className="cw-dot" />
                    </div>
                  </div>
                </div>
              )}

              {showQuick && !isTyping && (
                <div className="cw-quick">
                  {QUICK_REPLIES.map(q => (
                    <button key={q} className="cw-quick-btn" onClick={() => handleSend(q)}>{q}</button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="cw-input-area">
              <div className="cw-input-wrap">
                <input
                  ref={inputRef}
                  className="cw-input"
                  placeholder="Type a message"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  disabled={isTyping}
                />
              </div>
              <button
                className={`cw-send ${input.trim() ? 'active' : ''}`}
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>

            <div className="cw-powered">I'm Selling Support · Powered by AI</div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatWidget;